import { ADMIN_PORTAL_ROLES, ROLES } from '@giftsay/constants';
import { toast } from '@giftsay/ui';
import { onAuthStateChanged } from 'firebase/auth';
import * as React from 'react';

import { getApiErrorMessage } from '../../../lib/axios.js';
import { auth } from '../../../lib/firebase.js';
import { useAppDispatch } from '../../../store/hooks.js';
import { fetchMe } from '../services/auth-api.js';
import { signOut } from '../services/firebase-auth.js';
import { setAuthLoading, setAuthenticatedUser, setUnauthenticated } from '../store/auth-slice.js';

/**
 * Unlike customer-web, a successful Firebase sign-in is not enough here:
 * the account must already have a Firestore profile with an admin-portal
 * role (see ADMIN_PORTAL_ROLES) or the `vendor` role. Anyone else — a
 * customer account, a delivery partner, or a staff account not yet promoted
 * by a super_admin — is signed back out immediately with an explanatory
 * toast, never silently provisioned.
 *
 * `vendor` is deliberately not folded into ADMIN_PORTAL_ROLES — that constant
 * means "sees the staff portal shell" (AdminShell.tsx), which a vendor
 * doesn't. It's allowed through here as a separate case and routed to its
 * own minimal `/vendor` home (see router.tsx) instead.
 */
export function useAuthListener(): void {
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        dispatch(setUnauthenticated());
        return;
      }

      // Hold routing on "loading" until the portal profile arrives — otherwise a fresh sign-in
      // navigates while status is still "unauthenticated" and ProtectedRoute bounces it back to
      // /login (the "only works on the second try" bug).
      dispatch(setAuthLoading());
      void fetchMe()
        .then((user) => {
          const canAccessPortal =
            ADMIN_PORTAL_ROLES.includes(user.role) || user.role === ROLES.VENDOR;
          if (!canAccessPortal) {
            toast.error('This account does not have access to the admin portal');
            return signOut().then(() => dispatch(setUnauthenticated()));
          }
          dispatch(setAuthenticatedUser(user));
          return undefined;
        })
        .catch((error: unknown) => {
          toast.error(getApiErrorMessage(error, 'Could not load your admin profile'));
          return signOut().then(() => dispatch(setUnauthenticated()));
        });
    });

    return unsubscribe;
  }, [dispatch]);
}
